/**
 * Log a failed `httpsCallable` without paging Sentry for a refusal the user was
 * already shown.
 *
 * Every `console.error` is forwarded to Sentry as an error by
 * `handleConsoleError` (lib/datahub/errorLogging/consoleAndNetworkCapture.ts);
 * `console.warn` is not. A server that answers "an amendment is already open"
 * or "ticket S… was not found" has made a deliberate decision, and the
 * mutation's toast already tells the user why. Logging that as an error made
 * each such answer a new Sentry issue (OFFICEHUB-CLIENT-6G, OFFICEHUB-CLIENT-6K)
 * that nobody could fix, because nothing was broken.
 *
 * Only three codes are demoted, and only at call sites that opt in by using
 * this helper. They are the answers a server gives about the STATE of a record:
 *   - failed-precondition: the record is not in a state that allows the action
 *   - already-exists:      the thing being created is already there
 *   - not-found:           the record the action names does not exist
 *
 * Everything else keeps `console.error`, and therefore keeps paging:
 * `internal`, `unavailable`, `deadline-exceeded` (transport or server faults),
 * `permission-denied` / `unauthenticated` (a refusal that may be a rules or
 * role bug — OFFICEHUB-CLIENT-6B is exactly that), and `invalid-argument` (the
 * client sent something the server rejects — a client bug until shown
 * otherwise, OFFICEHUB-CLIENT-61).
 *
 * The warning carries the code, the server's message and the caller's ids, so
 * the refusal is still in the console and in the Sentry breadcrumb trail of any
 * later event from the same session.
 */

const EXPECTED_REFUSAL_CODES: ReadonlySet<string> = new Set([
  'functions/failed-precondition',
  'functions/already-exists',
  'functions/not-found',
]);

/** The callable's `functions/<code>` string, or null when `error` has none. */
export function callableCode(error: unknown): string | null {
  if (!error || typeof error !== 'object') return null;
  const { code } = error as { code?: unknown };
  return typeof code === 'string' ? code : null;
}

/** True when `error` is a callable refusal about the record's state (see module doc). */
export function isExpectedCallableRefusal(error: unknown): boolean {
  const code = callableCode(error);
  return code !== null && EXPECTED_REFUSAL_CODES.has(code);
}

/** The message of a non-Error value that still carries a callable code (`{ code, message }`); `String(obj)` would print "[object Object]". */
function plainMessage(error: unknown): string {
  const { message } = (error ?? {}) as { message?: unknown };
  return typeof message === 'string' ? message : String(error);
}

/**
 * `console.warn` for an expected refusal (with `ids`), `console.error` for
 * anything else — the latter exactly as the call site logged it before.
 */
export function logCallableFailure(
  label: string,
  error: unknown,
  ids: Readonly<Record<string, string | null | undefined>> = {},
): void {
  if (isExpectedCallableRefusal(error)) {
    const message = error instanceof Error ? error.message : plainMessage(error);
    console.warn(label, { code: callableCode(error), message, ...ids });
    return;
  }
  console.error(label, error);
}
